if (!window.__dcCalculatorV2Loaded) {
  window.__dcCalculatorV2Loaded = true;

/* ==========================================================================
   Calculator Config (v2)

   Same coverage-math engine as v1 (assets/custom-product-calculator.js),
   plus per-line pricing + an estimated total. Kept as a separate, fully
   self-contained file so v1 is never touched.

   "kit200" / "megaBundle1000" (and the 150/750 variants for Vibrance) are
   generic configs shared by every 2-item kit / 5-gallon mega bundle
   product family (ColorWave, Antiquing, Portico, PaveWave, CitrusEtch,
   AcquaTint). Unlike the plain stain/sealer products, these are sold as a
   SINGLE fixed-size SKU (color + sealer options only — there's no "1
   Gallon vs 5 Gallon" size option to match against), so `singleVariant`
   tells the engine to resolve one variant from the product's own options
   instead of trying to size-match a recipe across several sizes.
   ========================================================================== */
const CALCULATORS = {
  etch: {
    coverage: 200,
    unit: 'gallons',
    smallestUnit: 0.25,
    multipliers: [{ label: '1 Coat', value: 1, default: true }],
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 },
    ]
  },
  concreteStain: {
    coverage: 200,
    unit: 'gallons',
    smallestUnit: 0.03125,
    multipliers: [
      { label: '1 Coat', value: 0.5 },
      { label: '2 Coats (recommended)', value: 1, default: true },
      { label: '3 Coats (Very Porous)', value: 1.5 }
    ],
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 },
    ]
  },
  paverStain: {
    coverage: 200,
    unit: 'gallons',
    smallestUnit: 0.03125,
    multipliers: [
      { label: '1 Coat', value: 0.5 },
      { label: '2 Coats (recommended)', value: 1, default: true },
      { label: '3 Coats (Very Porous)', value: 1.5 }
    ],
    sizes: [
      { label: '1 Gallon', value: 1 },
    ]
  },
  penetratingSealer: {
    coverage: 200,
    unit: 'gallons',
    smallestUnit: 0.03125,
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 },
    ]
  },
  acrylicSealer: {
    coverage: 200,
    unit: 'gallons',
    smallestUnit: 0.03125,
    multipliers: [
      { label: '1 Coat', value: 0.5 },
      { label: '2 Coats (recommended)', value: 1, default: true },
      { label: '3 Coats', value: 1.5 }
    ],
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 },
    ]
  },
  concreteDye: {
    coverage: 150,
    unit: 'gallons',
    smallestUnit: 0.0390625,
    multipliers: [
      { label: '2–3 thin coats', value: 1, default: true },
      { label: '5 thin coats', value: 2 }
    ],
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 },
    ]
  },
  concreteFloorWax: {
    coverage: 1000,
    unit: 'gallons',
    smallestUnit: 0.03125,
    multipliers: [
      { label: '1 Coat', value: 1, default: true },
      { label: '2 Coats', value: 2 },
      { label: '3 Coats', value: 3 }
    ],
    sizes: [
      { label: '5 Gallons', value: 5 },
      { label: '1 Gallon', value: 1 }
    ]
  },
  colorbomb: {
    coverage: 26.7,
    unit: 'pounds',
    smallestUnit: 15,
    multipliers: [{ label: '1 Coat', value: 1, default: true }],
    sizes: [
      { label: '30 pounds', value: 30 },
    ]
  },

  /* ---- Generic single-SKU kit / mega bundle configs (new in v2) ---- */
  kit200: {
    coverage: 200,
    unit: 'kits',
    smallestUnit: 1,
    singleVariant: true,
    multipliers: [
      { label: '2 Coats (recommended)', value: 1, default: true },
      { label: '4 Coats', value: 2 }
    ],
    sizes: [
      { label: '1 Gallon Kit', value: 1 }
    ]
  },
  megaBundle1000: {
    coverage: 1000,
    unit: 'mega bundles',
    smallestUnit: 1,
    singleVariant: true,
    multipliers: [
      { label: '2 Coats (recommended)', value: 1, default: true },
      { label: '4 Coats', value: 2 }
    ],
    sizes: [
      { label: '5 Gallon Mega Bundle', value: 1 }
    ]
  },
  kit150: {
    coverage: 150,
    unit: 'kits',
    smallestUnit: 1,
    singleVariant: true,
    multipliers: [
      { label: '2–3 thin coats', value: 1, default: true },
      { label: '5 thin coats', value: 2 }
    ],
    sizes: [
      { label: '1 Gallon Kit', value: 1 }
    ]
  },
  megaBundle750: {
    coverage: 750,
    unit: 'mega bundles',
    smallestUnit: 1,
    singleVariant: true,
    multipliers: [
      { label: '2–3 thin coats', value: 1, default: true },
      { label: '5 thin coats', value: 2 }
    ],
    sizes: [
      { label: '5 Gallon Mega Bundle', value: 1 }
    ]
  }
};

const DEFAULT_CALCULATOR = {
  coverage: 1,
  unit: '',
  smallestUnit: 1,
  multipliers: [{ label: 'Default', value: 1, default: true }],
  sizes: []
};

/* ==========================================================================
   Utils
   ========================================================================== */

const getNumber = v => parseFloat(v) || 0;
const getCalculatorConfig = type => ({ ...DEFAULT_CALCULATOR, ...(CALCULATORS[type] || {}) });
const money = cents => '$' + ((parseFloat(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "ColorWave® 2-Step Concrete Stain System — 601 White / HydroCryl™ Gloss"
// instead of a generic size placeholder, once a variant is resolved.
function getProductLineLabel(variant, fallbackLabel) {
  const title = window.PRODUCT_TITLE || fallbackLabel || '';
  if (!variant) return title;
  const opts = [variant.option1, variant.option2, variant.option3].filter(v => v && v !== 'Default Title');
  return opts.length ? `${title} — ${opts.join(' / ')}` : title;
}

/* ==========================================================================
   Shopify Product Utils
   ========================================================================== */

async function getCurrentProductVariants() {
  if (window.PRODUCT_VARIANTS && Array.isArray(window.PRODUCT_VARIANTS)) {
    return window.PRODUCT_VARIANTS;
  }
  console.warn('⚠️ Product variants could not be obtained');
  return [];
}

function getProductOptions() {
  return Array.isArray(window.PRODUCT_OPTIONS) ? window.PRODUCT_OPTIONS : [];
}

function getSelectedProductOptions() {
  const options = {};

  document.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(input => {
    options[input.name.replace(/[-\d\s]/g, '')] = input.value;
  });

  document.querySelectorAll('.product-form__input select').forEach(select => {
    if (select.value) options[select.name || select.id || 'Option'] = select.value;
  });

  return options;
}

// The resolved variant id lives in the page's own product form
// (input[name="id"] or select[name="id"]) — reading it directly is the most
// reliable way to know "what's currently selected", no matter how the
// theme's variant picker renders its swatches/dropdowns.
//
// Scoped to the calculator's own section: other product forms can exist on
// the same page (the sticky add-to-cart bar renders its own #product-form-*
// duplicate), and an unscoped lookup can pick up one of those instead of the
// one the shopper is actually interacting with.
function getNativeVariant(calc, variants) {
  let sectionId = null;
  try {
    sectionId = window.FoxTheme?.utils?.getSectionId ? FoxTheme.utils.getSectionId(calc) : null;
  } catch (e) { /* fall back to an unscoped lookup */ }

  const scope = sectionId ? document.querySelector(`#product-form-${sectionId}`) : null;
  const els = (scope || document).querySelectorAll('input[name="id"], select[name="id"]');

  if (window.__dcCalcDebug) {
    console.debug('[dc-calc2] getNativeVariant', {
      sectionId,
      scopeFound: !!scope,
      candidates: Array.from(els).map(el => ({ tag: el.tagName, value: el.value })),
    });
  }

  for (const el of els) {
    const id = parseInt(el.value, 10);
    const variant = variants.find(v => v.id === id);
    if (variant) return variant;
  }
  return null;
}

/* ==========================================================================
   Shopify Cart Utils
   ========================================================================== */

function findVariantId(sizeLabel, variants, selectedOptions = {}) {
  if (!variants?.length) {
    console.error('❌ No variants available');
    return null;
  }

  const normalizedSizeLabel = sizeLabel.toLowerCase().trim();
  const selectedValues = Object.values(selectedOptions).map(v => v.toLowerCase().trim());
  selectedValues[0] = normalizedSizeLabel;

  const matches = variants.filter(v => {
    if (!v) return false;

    const variantOptions = [v.title, v.option1, v.option2, v.option3]
      .filter(Boolean)
      .map(s => s.toLowerCase().trim());

    const sizeMatch = variantOptions.some(opt =>
      opt.includes(normalizedSizeLabel) || normalizedSizeLabel.includes(opt)
    );

    if (!sizeMatch) return false;

    return selectedValues.every(sel =>
      variantOptions.some(opt => opt === sel || opt.includes(sel) || sel.includes(opt))
    );
  });

  if (!matches.length) {
    console.warn(`⚠️ No variant found for "${sizeLabel}"`, selectedOptions);
    return null;
  }

  if (matches.length > 1) {
    console.warn(`⚠️ Multiple matches found (${matches.length}), using first`);
  }

  return matches[0].id;
}

function findVariant(sizeLabel, variants, selectedOptions = {}) {
  const id = findVariantId(sizeLabel, variants, selectedOptions);
  if (!id) return null;
  return variants.find(v => v.id === id) || null;
}

async function postToCart(items) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.description || 'Failed to add to cart');
  }

  const data = await response.json();

  // Trigger cart refresh events for theme compatibility
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  window.dispatchEvent(new Event('cart:updated'));
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { items: data } }));

  return data;
}

async function updateCartUI() {
  try {
    const { item_count } = await fetch('/cart.js').then(r => r.json());

    document.querySelectorAll('.cart-count, cart-count, [data-cart-count]').forEach(counter => {
      const oldCount = parseInt(counter.textContent) || 0;
      counter.textContent = item_count;
      counter.setAttribute('aria-label', `${item_count} items`);

      if (item_count > 0) {
        counter.style.display = '';
        counter.removeAttribute('hidden');
        counter.classList.remove('hidden');
      }

      if (item_count > oldCount) {
        counter.classList.add('cart-count--updated');
        setTimeout(() => counter.classList.remove('cart-count--updated'), 600);
      }
    });

    // Re-render the cart drawer through the theme's own component instead of
    // hand-rolling the markup — this is what keeps the drawer in sync and
    // avoids the desync bug the one-off custom_liquid calculator has.
    const cartDrawer = document.querySelector('#CartDrawer');
    if (!cartDrawer) return;

    if (typeof cartDrawer.onCartRefresh === 'function') {
      await cartDrawer.onCartRefresh({ detail: { open: true } });
    }

    await new Promise(resolve => setTimeout(resolve, 400));

    const closeBtn = cartDrawer.querySelector('.drawer__close-btn');
    if (closeBtn) {
      const freshBtn = closeBtn.cloneNode(true);
      closeBtn.replaceWith(freshBtn);
      freshBtn.addEventListener('click', () => {
        if (typeof cartDrawer.hide === 'function') {
          cartDrawer.hide();
        } else {
          cartDrawer.removeAttribute('open');
          cartDrawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('overflow-hidden');
        }
      });
    }

  } catch (error) {
    console.error('❌ Error updating cart UI:', error);
  }
}

/* ==========================================================================
   Match the theme's real "Add to cart" red for the accent color, same idea
   as the EverStain custom_liquid calculator.
   ========================================================================== */
function applyThemeAccent(calc) {
  try {
    const sels = [
      'product-form button[type="submit"]', '.product-form__submit',
      'button[name="add"]', 'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] [type="submit"]'
    ];
    for (const sel of sels) {
      const btn = document.querySelector(sel);
      if (!btn) continue;
      const color = getComputedStyle(btn).backgroundColor;
      if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
        calc.style.setProperty('--dc-calc2-accent', color);
        return;
      }
    }
  } catch (e) { /* fall back to the CSS default */ }
}

/* ==========================================================================
   Init
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Scoped to plain recipe calculators only — complete-kit-calculator.js
  // handles [data-ck-calculator] instances, even though both share the same
  // .dc-calculator2 visual language.
  document.querySelectorAll('.dc-calculator2[data-calculator]').forEach(initCalculator);
});

function initCalculator(calc) {
  const config = getCalculatorConfig(calc.dataset.calculator);
  const $ = sel => calc.querySelector(sel);

  applyThemeAccent(calc);

  const areaEl = $('[data-result="area"]');
  const recipeBody = $('[data-result="recipe"]');
  const grandTotalEl = $('[data-result="grand-total"]');
  const addToCartBtn = $('[data-add-to-cart]');
  const coatsWrapper = $('[data-coats-wrapper]');
  const variantOptionsWrapper = $('[data-variant-options]');
  const variantOptionsHeading = $('[data-variant-options-heading]');

  const inputs = {
    length: $('[data-input="length"]'),
    width: $('[data-input="width"]'),
    coats: $('[data-input="coats"]')
  };

  let currentRecipe = [];
  let productVariants = [];
  const productOptions = getProductOptions(); // [{ name, values }, ...] in option1/2/3 order

  // Single-variant kits only: which color/sealer/etc. the calculator itself
  // has selected. Starts out following the page's native variant picker;
  // stops following as soon as the shopper touches the calculator's own
  // selects — but only until the shopper changes the native picker again,
  // which always wins (see syncFromNativeVariant below).
  let calcSelectedOptions = {};
  let userOverrodeOptions = false;
  let lastNativeVariantId = null;

  setup();
  renderEmptyRecipe();
  calculate();

  calc.querySelectorAll('input, select').forEach(el => {
    el.addEventListener('input', calculate);
    el.addEventListener('change', calculate);
  });

  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', handleAddToCart);
  }
  loadProductVariants();

  /* ============================
     Setup
     ============================ */

  function setup() {
    setupCoats();
    if (config.singleVariant) setupVariantOptions();
  }

  function setupCoats() {
    if (!inputs.coats) return;

    const options = config.multipliers || [];
    if (options.length <= 1) {
      coatsWrapper?.style.setProperty('display', 'none');
      inputs.coats.value = options[0]?.value || 1;
      return;
    }

    inputs.coats.innerHTML = options.map(o => `<option value="${o.value}">${o.label}</option>`).join('');
    inputs.coats.value = (options.find(o => o.default) || options[0]).value;
  }

  function setupVariantOptions() {
    if (!variantOptionsWrapper || !productOptions.length) return;

    // Only build pickers for options that actually vary (skip a single-value option).
    const pickable = productOptions.filter(opt => opt.values && opt.values.length > 1);
    if (!pickable.length) return;

    if (variantOptionsHeading) variantOptionsHeading.hidden = false;
    variantOptionsWrapper.hidden = false;
    variantOptionsWrapper.innerHTML = pickable.map((opt, i) => `
      <div class="dc-calculator2__variant-option">
        <select data-variant-option="${opt.name}" id="calc2-opt-${i}-${calc.dataset.calculator}" aria-label="${opt.name}">
          ${opt.values.map(v => `<option value="${v}">${v}</option>`).join('')}
        </select>
      </div>
    `).join('');

    variantOptionsWrapper.querySelectorAll('[data-variant-option]').forEach(select => {
      select.addEventListener('change', () => {
        userOverrodeOptions = true;
        calcSelectedOptions[select.dataset.variantOption] = select.value;
        calculate();
      });
    });

    syncFromNativeVariant();
    // The native picker can trigger a full section re-render on change;
    // poll instead of trying to hook into that flow directly.
    setInterval(syncFromNativeVariant, 600);
  }

  function syncFromNativeVariant() {
    if (!productVariants.length) return;
    const nativeVariant = getNativeVariant(calc, productVariants);

    if (window.__dcCalcDebug) {
      console.debug('[dc-calc2] syncFromNativeVariant tick', {
        nativeVariantId: nativeVariant?.id,
        lastNativeVariantId,
        userOverrodeOptions,
      });
    }

    if (!nativeVariant) return;

    if (nativeVariant.id !== lastNativeVariantId) {
      // The shopper just picked something in the page's own variant picker
      // (or this is the first run) — that always wins over a previous
      // calculator-only selection.
      lastNativeVariantId = nativeVariant.id;
      userOverrodeOptions = false;
    }

    if (userOverrodeOptions) return;

    productOptions.forEach((opt, i) => {
      const value = nativeVariant[`option${i + 1}`];
      if (value == null) return;
      calcSelectedOptions[opt.name] = value;
      const select = variantOptionsWrapper?.querySelector(`[data-variant-option="${CSS.escape(opt.name)}"]`);
      if (select && select.value !== value) select.value = value;
    });

    calculate();
  }

  function getVariantFromCalcOptions() {
    if (!productVariants.length || !productOptions.length) return null;
    return productVariants.find(v =>
      productOptions.every((opt, i) => {
        const selected = calcSelectedOptions[opt.name];
        return selected == null || v[`option${i + 1}`] === selected;
      })
    ) || null;
  }

  /* ============================
     Variants
     ============================ */

  async function loadProductVariants() {
    try {
      productVariants = await getCurrentProductVariants();
      if (!productVariants.length) {
        console.warn('⚠️ No variants loaded.');
      } else {
        if (config.singleVariant && !Object.keys(calcSelectedOptions).length) {
          syncFromNativeVariant();
        }
        // Re-render with pricing now that variant data (and prices) are available.
        calculate();
      }
    } catch (error) {
      console.error('Error loading variants:', error);
    }
  }

  /* ============================
     Calculations
     ============================ */

  function getArea() {
    return getNumber(inputs.length?.value) * getNumber(inputs.width?.value);
  }

  function getNeededAmount(area) {
    return (area / config.coverage) * (getNumber(inputs.coats?.value) || 1);
  }

  function roundToBuyable(amount) {
    return Math.ceil(amount / config.smallestUnit) * config.smallestUnit;
  }

  function calculate() {
    const area = getArea();
    const needed = getNeededAmount(area);
    const buyable = roundToBuyable(needed);

    if (areaEl) areaEl.textContent = area ? Math.round(area).toLocaleString('en-US') : '0';

    renderRecipe(buyable);
  }

  /* ============================
     Rendering
     ============================ */

  function renderEmptyRecipe() {
    if (!recipeBody) return;
    recipeBody.innerHTML = `<tr><td colspan="3" class="dc-calculator2__empty-message">Enter your dimensions to see exactly what you need.</td></tr>`;
    if (grandTotalEl) grandTotalEl.textContent = '—';
    currentRecipe = [];
    updateAddToCartButton();
  }

  function renderRecipe(total) {
    if (!recipeBody) return;

    if (!total || total <= 0) {
      renderEmptyRecipe();
      return;
    }

    recipeBody.innerHTML = '';
    currentRecipe = [];
    let grandTotalCents = 0;
    let pricingComplete = productVariants.length > 0;

    if (config.singleVariant) {
      // One SKU, no size dimension — the whole "recipe" is just N units of
      // whatever color/sealer combo is currently selected.
      const size = config.sizes[0];
      const qty = Math.ceil(total / size.value);
      const variant = pricingComplete ? (getVariantFromCalcOptions() || getNativeVariant(calc, productVariants)) : null;
      currentRecipe.push({ quantity: qty });

      let subtotalText = '—';
      if (pricingComplete) {
        if (variant) {
          const lineCents = (parseFloat(variant.price) || 0) * qty;
          grandTotalCents += lineCents;
          subtotalText = money(lineCents);
        } else {
          pricingComplete = false;
        }
      }

      recipeBody.insertAdjacentHTML(
        'beforeend',
        `<tr><td>${getProductLineLabel(variant, size.label)}</td><td>${qty}</td><td>${subtotalText}</td></tr>`
      );
    } else {
      const selectedOptions = getSelectedProductOptions();

      // Not every product configured with this calculator_type actually
      // sells every size in it (e.g. EasyCureSeal only comes in 5 Gallons,
      // even though the shared "acrylicSealer" config also lists 1 Gallon).
      // Only build the recipe out of sizes that exist as a real variant —
      // once variants have loaded — so we never recommend buying a size
      // that isn't for sale.
      const availableSizes = productVariants.length
        ? config.sizes.filter(size => findVariant(size.label, productVariants, selectedOptions))
        : config.sizes;
      const sizesToUse = availableSizes.length ? availableSizes : config.sizes;
      let remaining = total;

      [...sizesToUse]
        .sort((a, b) => b.value - a.value)
        .forEach((size, i, arr) => {
          const isLast = i === arr.length - 1;
          const qty = isLast ? Math.ceil(remaining / size.value) : Math.floor(remaining / size.value);
          if (qty <= 0) return;

          remaining -= qty * size.value;
          currentRecipe.push({ sizeLabel: size.label, sizeValue: size.value, quantity: qty });

          let subtotalText = '—';
          if (pricingComplete) {
            const variant = findVariant(size.label, productVariants, selectedOptions);
            if (variant) {
              const lineCents = (parseFloat(variant.price) || 0) * qty;
              grandTotalCents += lineCents;
              subtotalText = money(lineCents);
            } else {
              pricingComplete = false;
            }
          }

          recipeBody.insertAdjacentHTML(
            'beforeend',
            `<tr><td>${size.label}</td><td>${qty}</td><td>${subtotalText}</td></tr>`
          );
        });
    }

    if (grandTotalEl) grandTotalEl.textContent = pricingComplete ? money(grandTotalCents) : '—';

    updateAddToCartButton();
    checkRecipeAvailability();
  }

  function updateAddToCartButton() {
    if (!addToCartBtn) return;
    // Always visible — just disabled until there's something to add.
    addToCartBtn.disabled = currentRecipe.length === 0;
  }

  /* ============================
     Availability
     ============================ */

  function checkRecipeAvailability() {
    if (!addToCartBtn || !currentRecipe.length || !productVariants.length) return;

    let available;
    if (config.singleVariant) {
      const variant = getVariantFromCalcOptions() || getNativeVariant(calc, productVariants);
      available = variant?.available !== false;
    } else {
      const selectedOptions = getSelectedProductOptions();
      available = currentRecipe.every(item => {
        const variant = findVariant(item.sizeLabel, productVariants, selectedOptions);
        return variant?.available !== false;
      });
    }

    available ? setInStock() : setOutOfStock();
  }

  function setOutOfStock() {
    if (!addToCartBtn) return;
    addToCartBtn.disabled = true;
    addToCartBtn.style.opacity = '0.5';
    addToCartBtn.style.cursor = 'not-allowed';
    setBtnState('Out of Stock');
  }

  function setInStock() {
    if (!addToCartBtn) return;
    addToCartBtn.disabled = false;
    addToCartBtn.style.opacity = '';
    addToCartBtn.style.cursor = '';
    setBtnState('Add Recipe to Cart');
  }

  /* ============================
     Add to Cart
     ============================ */

  function setBtnState(text, color = '') {
    const btnText = addToCartBtn.querySelector('.btn__text');
    if (!btnText) return;
    btnText.textContent = text;
    btnText.style.color = color;
  }

  function resetBtn(delay) {
    setTimeout(() => {
      setBtnState('Add Recipe to Cart');
      addToCartBtn.disabled = false;
    }, delay);
  }

  async function handleAddToCart() {
    if (!addToCartBtn || !currentRecipe.length) return;

    addToCartBtn.disabled = true;
    addToCartBtn.classList.add('btn--loading');

    try {
      if (!productVariants.length) await loadProductVariants();
      if (!productVariants.length) throw new Error('Could not load product variants. Please refresh.');

      const cartItems = [];

      if (config.singleVariant) {
        const variant = getVariantFromCalcOptions() || getNativeVariant(calc, productVariants);
        if (variant) cartItems.push({ id: variant.id, quantity: currentRecipe[0].quantity });
      } else {
        const selectedOptions = getSelectedProductOptions();
        for (const item of currentRecipe) {
          const variantId = findVariantId(item.sizeLabel, productVariants, selectedOptions);
          if (variantId) cartItems.push({ id: variantId, quantity: item.quantity });
        }
      }

      if (!cartItems.length) throw new Error('No valid variants found.');

      await postToCart(cartItems);
      await updateCartUI();

      setBtnState('Added to Cart!');
      resetBtn(2000);

    } catch (error) {
      console.error('❌ Error:', error);
      setBtnState('Error - Try Again', 'var(--color-error, #ff1100)');
      resetBtn(3000);

    } finally {
      addToCartBtn.classList.remove('btn--loading');
    }
  }
}

} // end window.__dcCalculatorV2Loaded guard
